// ForgotPasswordComponent.js
import React, { useEffect, useRef } from "react";
import { useAtom } from "jotai";

import {
  useForgotPassword,
  useI18N,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import Loader from "@/components/common/Loader";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shadcn/components/ui/alert";
import { emailAtom } from "@/store/atoms";
import { useDarkMode } from "@/hooks/useDarkMode";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import WideButton from "@/components/common/WideButton";
import { ArrowLeft } from "lucide-react";
import { StandardButton } from "@/components/common/StandardButton";
import { createPageAnalytics } from "common/analytics";

interface EnterPasswordProps {
  onClose: () => void;
  title: string;
}

const ForgotPasswordComponent = ({ onClose, title }: EnterPasswordProps) => {
  const isZoomedIn = useZoomDetection();
  const i18n = useI18N();
  const isDark = useDarkMode();
  const [email] = useAtom(emailAtom);
  const { modalStyle, modalClassName } = useModalHeight();
  const { forgotPassword, error, isLoading, result } = useForgotPassword();

  const { setScreenIndex } = useAuthModals();

  const passwordResetRequestAnalytics = createPageAnalytics(
    "password_reset_request_form",
  );

  const handleContinue = async () => {
    // Generated tracking plan: continue_action_button -> password_reset_requested
    passwordResetRequestAnalytics.track("continue_action_button", {});

    forgotPassword({ email });
  };

  useEffect(() => {
    // Generated tracking plan: page_view_page -> password_reset_request_form_opened
    passwordResetRequestAnalytics.track("page_view_page", {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (result?.status) {
      console.log("forgotPassword done");
    }
  }, [result, setScreenIndex, onClose, email]);

  const backToPassword = async () => {
    // Generated tracking plan: back_action_button -> password_reset_request_form_closed
    passwordResetRequestAnalytics.track("back_action_button", {});

    //move to enter password
    setScreenIndex(AuthModalScreen.EnterPassword);
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <section
      style={modalStyle}
      className="flex flex-col bg-bg-brand-secondary-default rounded-xl overflow-y-auto"
      role="none"
      aria-labelledby="modal-title"
    >
      <header
        className={`py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
      >
        {!result?.status && (
          <StandardButton
            onClick={backToPassword}
            variant="back"
            icon={<ArrowLeft color={isDark ? "#A39FE8" : "#352FC3"} />}
            aria-label="Go back"
            tabIndex={0}
          />
        )}

        <h1
          className="title-s-semibold text-center text-fg-neutral-strong"
          ref={firstFocusRef}
          tabIndex={0}
          id="modal-title"
        >
          {title}
        </h1>
      </header>

      <div className="flex-1 px-8 md:px-16 overflow-y-auto">
        <div className="flex justify-center my-6">
          <img
            src="/images/Group.svg"
            alt=""
            className="w-[100px] md:w-[120px]"
            aria-hidden="true"
          />
        </div>
        <h2 className="title-m-bold">It's okay! We can help!</h2>
        <h3 className="title-s-semibold mt-4 text-fg-neutral-strong">
          Enter your email address below and we’ll get you and email to reset
          your password.
        </h3>
        <div className="my-6 border-b border-b-primary-150 w-full"></div>
        <div className="mb-6">
          <FloatingLabelInput
            readonly
            label="Email"
            required
            type="email"
            value={email}
            placeholder="Email"
            onChange={() => {}}
            errorMessage={
              error?.message
                ? "You've tried to use forgot password too many times. Please try again in 5 minutes."
                : undefined
            }
          />
        </div>
        <div className="mb-8">
          <span className="label-m-medium text-fg-neutral-strong">
            Already know your details? You can{" "}
          </span>
          <a
            className=" label-m-medium underline mr-1 cursor-pointer"
            onClick={() => {
              setScreenIndex(AuthModalScreen.EnterEmail);
            }}
          >
            Log in here
          </a>
        </div>
        <div className="flex-1">
          {isLoading && <Loader />}

          {result?.status && (
            <Alert className="mb-6 mt-2">
              <AlertTitle></AlertTitle>
              <AlertDescription>{result?.data.message}</AlertDescription>
            </Alert>
          )}
        </div>
        <div className="mb-16">
          <WideButton
            type="filled"
            disabled={email.length <= 0}
            onClick={
              result?.status
                ? () => setScreenIndex(AuthModalScreen.EnterEmail)
                : handleContinue
            }
          >
            {i18n.get("continue")}
          </WideButton>
        </div>
      </div>
    </section>
  );
};

export default ForgotPasswordComponent;
